import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Input,
  Checkbox,
  Button,
  VStack,
  HStack,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Divider,
  useToast,
  Image,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import { FaDownload, FaPrint } from "react-icons/fa";
import { generateAandLPDF } from "./components/generateAandLPDF";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { brandConfig } from "../../../brandConfig";

export interface AandLFormData {
  // Applicant A
  nameA: string;
  addressA: string;
  residenceYearsA: string;
  stateA: string;
  postcodeA: string;
  phoneHomeA: string;
  mobileA: string;
  dobA: string;
  occupationA: string;
  employerA: string;
  empFITA: boolean;
  empPTA: boolean;
  empCasualA: boolean;
  taxableIncomeA: string;
  dependentsA: string;
  agesA: string;

  // Applicant B
  nameB: string;
  addressB: string;
  residenceYearsB: string;
  stateB: string;
  postcodeB: string;
  phoneHomeB: string;
  mobileB: string;
  dobB: string;
  occupationB: string;
  employerB: string;
  empFITB: boolean;
  empPTB: boolean;
  empCasualB: boolean;
  taxableIncomeB: string;
  dependentsB: string;
  agesB: string;

  // Self Employed & Income
  selfEmployedYes: boolean;
  selfEmployedNo: boolean;
  grossTurnover: string;
  taxReturnsYes: boolean;
  taxReturnsNo: boolean;
  tradingName: string;
  netTaxableIncome: string;
  incomeSplitYes: boolean;
  incomeSplitNo: boolean;
  incomeSplitAmount: string;

  // Assets (Value, Owner/Title, Rent p.w.)
  assetHouseValue: string;
  assetHouseOwner: string;
  assetHouseRent: string;
  assetProp1Value: string;
  assetProp1Owner: string;
  assetProp1Rent: string;
  assetProp2Value: string;
  assetProp2Owner: string;
  assetProp2Rent: string;
  assetProp3Value: string;
  assetProp3Owner: string;
  assetProp3Rent: string;
  assetOtherProp1Value: string;
  assetOtherProp1Owner: string;
  assetOtherProp1Rent: string;
  assetOtherProp2Value: string;
  assetOtherProp2Owner: string;
  assetOtherProp2Rent: string;
  assetOtherProp3Value: string;
  assetOtherProp3Owner: string;
  assetOtherProp3Rent: string;
  assetSuperValue: string;
  assetSuperOwner: string;
  assetSharesValue: string;
  assetSharesOwner: string;
  assetSavingsValue: string;
  assetSavingsOwner: string;
  assetOther1Value: string;
  assetOther1Owner: string;
  assetOther2Value: string;
  assetOther2Owner: string;

  // Liabilities (Lender, Balance, Limit Monthly, Repayment)
  liabHouseLender: string;
  liabHouseBalance: string;
  liabHouseLimit: string;
  liabHouseRepayment: string;
  liabProp1Lender: string;
  liabProp1Balance: string;
  liabProp1Limit: string;
  liabProp1Repayment: string;
  liabProp2Lender: string;
  liabProp2Balance: string;
  liabProp2Limit: string;
  liabProp2Repayment: string;
  liabProp3Lender: string;
  liabProp3Balance: string;
  liabProp3Limit: string;
  liabProp3Repayment: string;
  liabPersonalLender: string;
  liabPersonalBalance: string;
  liabPersonalLimit: string;
  liabPersonalRepayment: string;
  liabCC1Lender: string;
  liabCC1Balance: string;
  liabCC1Limit: string;
  liabCC1Repayment: string;
  liabCC2Lender: string;
  liabCC2Balance: string;
  liabCC2Limit: string;
  liabCC2Repayment: string;
  liabHPLender: string;
  liabHPBalance: string;
  liabHPLimit: string;
  liabHPRepayment: string;
  liabOverdraftLender: string;
  liabOverdraftBalance: string;
  liabOverdraftLimit: string;
  liabOverdraftRepayment: string;
  liabPrivateHealthLender: string;
  liabPrivateHealthBalance: string;
  liabPrivateHealthLimit: string;
  liabPrivateHealthRepayment: string;
  liabPrivateSchoolLender: string;
  liabPrivateSchoolBalance: string;
  liabPrivateSchoolLimit: string;
  liabPrivateSchoolRepayment: string;
  liabChildCareLender: string;
  liabChildCareBalance: string;
  liabChildCareLimit: string;
  liabChildCareRepayment: string;
  liabOther1Lender: string;
  liabOther1Balance: string;
  liabOther1Limit: string;
  liabOther1Repayment: string;
  liabOther2Lender: string;
  liabOther2Balance: string;
  liabOther2Limit: string;
  liabOther2Repayment: string;
  liabOther3Lender: string;
  liabOther3Balance: string;
  liabOther3Limit: string;
  liabOther3Repayment: string;

  // Signatures
  consultantName: string;
  date: string;
}

const emptyFormData: AandLFormData = {
  nameA: "", addressA: "", residenceYearsA: "", stateA: "", postcodeA: "",
  phoneHomeA: "", mobileA: "", dobA: "", occupationA: "", employerA: "",
  empFITA: false, empPTA: false, empCasualA: false,
  taxableIncomeA: "", dependentsA: "", agesA: "",
  nameB: "", addressB: "", residenceYearsB: "", stateB: "", postcodeB: "",
  phoneHomeB: "", mobileB: "", dobB: "", occupationB: "", employerB: "",
  empFITB: false, empPTB: false, empCasualB: false,
  taxableIncomeB: "", dependentsB: "", agesB: "",
  selfEmployedYes: false, selfEmployedNo: false, grossTurnover: "",
  taxReturnsYes: false, taxReturnsNo: false, tradingName: "",
  netTaxableIncome: "", incomeSplitYes: false, incomeSplitNo: false, incomeSplitAmount: "",
  assetHouseValue: "", assetHouseOwner: "", assetHouseRent: "",
  assetProp1Value: "", assetProp1Owner: "", assetProp1Rent: "",
  assetProp2Value: "", assetProp2Owner: "", assetProp2Rent: "",
  assetProp3Value: "", assetProp3Owner: "", assetProp3Rent: "",
  assetOtherProp1Value: "", assetOtherProp1Owner: "", assetOtherProp1Rent: "",
  assetOtherProp2Value: "", assetOtherProp2Owner: "", assetOtherProp2Rent: "",
  assetOtherProp3Value: "", assetOtherProp3Owner: "", assetOtherProp3Rent: "",
  assetSuperValue: "", assetSuperOwner: "",
  assetSharesValue: "", assetSharesOwner: "",
  assetSavingsValue: "", assetSavingsOwner: "",
  assetOther1Value: "", assetOther1Owner: "",
  assetOther2Value: "", assetOther2Owner: "",
  liabHouseLender: "", liabHouseBalance: "", liabHouseLimit: "", liabHouseRepayment: "",
  liabProp1Lender: "", liabProp1Balance: "", liabProp1Limit: "", liabProp1Repayment: "",
  liabProp2Lender: "", liabProp2Balance: "", liabProp2Limit: "", liabProp2Repayment: "",
  liabProp3Lender: "", liabProp3Balance: "", liabProp3Limit: "", liabProp3Repayment: "",
  liabPersonalLender: "", liabPersonalBalance: "", liabPersonalLimit: "", liabPersonalRepayment: "",
  liabCC1Lender: "", liabCC1Balance: "", liabCC1Limit: "", liabCC1Repayment: "",
  liabCC2Lender: "", liabCC2Balance: "", liabCC2Limit: "", liabCC2Repayment: "",
  liabHPLender: "", liabHPBalance: "", liabHPLimit: "", liabHPRepayment: "",
  liabOverdraftLender: "", liabOverdraftBalance: "", liabOverdraftLimit: "", liabOverdraftRepayment: "",
  liabPrivateHealthLender: "", liabPrivateHealthBalance: "", liabPrivateHealthLimit: "", liabPrivateHealthRepayment: "",
  liabPrivateSchoolLender: "", liabPrivateSchoolBalance: "", liabPrivateSchoolLimit: "", liabPrivateSchoolRepayment: "",
  liabChildCareLender: "", liabChildCareBalance: "", liabChildCareLimit: "", liabChildCareRepayment: "",
  liabOther1Lender: "", liabOther1Balance: "", liabOther1Limit: "", liabOther1Repayment: "",
  liabOther2Lender: "", liabOther2Balance: "", liabOther2Limit: "", liabOther2Repayment: "",
  liabOther3Lender: "", liabOther3Balance: "", liabOther3Limit: "", liabOther3Repayment: "",
  consultantName: "", date: "",
};

const AandLForm: React.FC = () => {
  const toast = useToast();
  const [formData, setFormData] = useState<AandLFormData>(emptyFormData);

  const handleInputChange = (field: keyof AandLFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    toast({
      title: "Generating PDF",
      description: "Your fillable PDF is being generated...",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
    try {
      await generateAandLPDF(formData);
      toast({
        title: "PDF Downloaded",
        description: "Your Assets & Liabilities PDF has been saved.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("PDF generation error:", error);
      toast({
        title: "PDF Generation Failed",
        description: "There was an error generating the PDF. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const inputStyle = {
    bg: brandConfig.components.form.fieldBg,
    border: "1px solid",
    borderColor: "rgba(0, 0, 0, 0.12)",
    _hover: { borderColor: "rgba(0, 0, 0, 0.25)" },
    _focus: {
      borderColor: brandConfig.components.form.fieldBorderFocus,
      boxShadow: brandConfig.components.form.fieldShadowFocus,
    },
  };

  const thStyle = {
    bg: brandConfig.colors.primary,
    color: "white",
    fontSize: "xs",
    fontWeight: "bold",
    textAlign: "center" as const,
    px: 2,
    py: 1,
  };

  const tdLabelStyle = {
    fontSize: "xs",
    fontWeight: "semibold",
    color: brandConfig.colors.text.primary,
    whiteSpace: "nowrap" as const,
    px: 2,
    py: 1,
  };

  const tdInputStyle = {
    px: 1,
    py: 1,
  };

  // Reusable asset row
  const AssetRow = ({
    label,
    valueField,
    ownerField,
    rentField,
  }: {
    label: string;
    valueField: keyof AandLFormData;
    ownerField: keyof AandLFormData;
    rentField?: keyof AandLFormData;
  }) => (
    <Tr>
      <Td {...tdLabelStyle}>{label}</Td>
      <Td {...tdInputStyle}>
        <Input
          value={formData[valueField] as string}
          onChange={(e) => handleInputChange(valueField, e.target.value)}
          {...inputStyle}
          size="xs"
          placeholder="$"
        />
      </Td>
      <Td {...tdInputStyle}>
        <Input
          value={formData[ownerField] as string}
          onChange={(e) => handleInputChange(ownerField, e.target.value)}
          {...inputStyle}
          size="xs"
        />
      </Td>
      <Td {...tdInputStyle}>
        {rentField ? (
          <Input
            value={formData[rentField] as string}
            onChange={(e) => handleInputChange(rentField, e.target.value)}
            {...inputStyle}
            size="xs"
            placeholder="$"
          />
        ) : (
          <Box h="24px" bg="gray.200" borderRadius="md" />
        )}
      </Td>
    </Tr>
  );

  // Reusable liability row
  const LiabRow = ({
    label,
    lenderField,
    balanceField,
    limitField,
    repayField,
  }: {
    label: string;
    lenderField: keyof AandLFormData;
    balanceField: keyof AandLFormData;
    limitField: keyof AandLFormData;
    repayField: keyof AandLFormData;
  }) => (
    <Tr>
      <Td {...tdLabelStyle}>{label}</Td>
      <Td {...tdInputStyle}>
        <Input
          value={formData[lenderField] as string}
          onChange={(e) => handleInputChange(lenderField, e.target.value)}
          {...inputStyle}
          size="xs"
        />
      </Td>
      <Td {...tdInputStyle}>
        <Input
          value={formData[balanceField] as string}
          onChange={(e) => handleInputChange(balanceField, e.target.value)}
          {...inputStyle}
          size="xs"
          placeholder="$"
        />
      </Td>
      <Td {...tdInputStyle}>
        <Input
          value={formData[limitField] as string}
          onChange={(e) => handleInputChange(limitField, e.target.value)}
          {...inputStyle}
          size="xs"
          placeholder="$"
        />
      </Td>
      <Td {...tdInputStyle}>
        <Input
          value={formData[repayField] as string}
          onChange={(e) => handleInputChange(repayField, e.target.value)}
          {...inputStyle}
          size="xs"
          placeholder="$"
        />
      </Td>
    </Tr>
  );

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/resources/opa-form"
        fallbackTitle="Assets & Liabilities Form"
        fallbackDescription="Fill out the Assets & Liabilities form for One Property Australia"
      />

      <Box className="no-print">
        <NavbarWithCallToAction />
      </Box>

      <Container maxW="container.xl" py={8}>
        {/* Action Buttons */}
        <HStack spacing={4} mb={6} className="no-print">
          <Button leftIcon={<FaPrint />} bg={brandConfig.colors.primary} color="white" _hover={{ bg: brandConfig.colors.primaryHover }} onClick={handlePrint}>
            Print Form
          </Button>
          <Button leftIcon={<FaDownload />} bg="#0D7F6E" color="white" _hover={{ bg: "#0a6459" }} onClick={handleDownloadPDF}>
            Download as PDF
          </Button>
        </HStack>

        {/* Printable Form */}
        <Box id="al-form-printable" bg="white" p={8} border="1px solid" borderColor="rgba(0, 0, 0, 0.1)" borderRadius="md">

          {/* Header */}
          <HStack justify="space-between" mb={6} align="flex-start">
            <Box flexShrink={0}>
              <Image
                src="/assets/One-logo.png"
                alt="One Property Australia"
                h="80px"
                w="auto"
                objectFit="contain"
              />
            </Box>
            <Heading size="lg" textAlign="center" flex={1} alignSelf="center">
              ASSETS &amp; LIABILITIES
            </Heading>
            <VStack align="flex-end" spacing={0} fontSize="xs" flexShrink={0}>
              <Text fontWeight="bold">One Property Australia</Text>
              <Text>ACN: 664 898 846</Text>
              <Text>6/22 Alexandra Pl,</Text>
              <Text>Murarrie QLD 4172</Text>
              <Text>ONEPROPERTYAUSTRALIA.COM.AU</Text>
            </VStack>
          </HStack>

          <Divider my={4} />

          {/* ── APPLICANT DETAILS ── */}
          <Box bg={brandConfig.colors.primary} px={3} py={1} mb={4} borderRadius="sm">
            <Text color="white" fontWeight="bold" fontSize="sm">APPLICANT DETAILS</Text>
          </Box>

          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            {/* Applicant A */}
            <GridItem>
              <Text fontWeight="bold" fontSize="sm" color={brandConfig.colors.primary} mb={3}>Applicant A</Text>
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="xs">Name:</FormLabel>
                  <Input value={formData.nameA} onChange={(e) => handleInputChange("nameA", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Address:</FormLabel>
                  <Input value={formData.addressA} onChange={(e) => handleInputChange("addressA", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                  <FormControl>
                    <FormLabel fontSize="xs">Residence (Yrs):</FormLabel>
                    <Input value={formData.residenceYearsA} onChange={(e) => handleInputChange("residenceYearsA", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">State:</FormLabel>
                    <Input value={formData.stateA} onChange={(e) => handleInputChange("stateA", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">P/Code:</FormLabel>
                    <Input value={formData.postcodeA} onChange={(e) => handleInputChange("postcodeA", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                </Grid>
                <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                  <FormControl>
                    <FormLabel fontSize="xs">Ph (H):</FormLabel>
                    <Input value={formData.phoneHomeA} onChange={(e) => handleInputChange("phoneHomeA", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Mobile:</FormLabel>
                    <Input value={formData.mobileA} onChange={(e) => handleInputChange("mobileA", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">D.O.B:</FormLabel>
                    <Input value={formData.dobA} onChange={(e) => handleInputChange("dobA", e.target.value)} placeholder="DD/MM/YYYY" {...inputStyle} size="sm" />
                  </FormControl>
                </Grid>
                <FormControl>
                  <FormLabel fontSize="xs">Occupation:</FormLabel>
                  <Input value={formData.occupationA} onChange={(e) => handleInputChange("occupationA", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Employer:</FormLabel>
                  <Input value={formData.employerA} onChange={(e) => handleInputChange("employerA", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold">Employment:</FormLabel>
                  <HStack spacing={4}>
                    <Checkbox isChecked={formData.empFITA} onChange={(e) => handleInputChange("empFITA", e.target.checked)}>F/T</Checkbox>
                    <Checkbox isChecked={formData.empPTA} onChange={(e) => handleInputChange("empPTA", e.target.checked)}>P/T</Checkbox>
                    <Checkbox isChecked={formData.empCasualA} onChange={(e) => handleInputChange("empCasualA", e.target.checked)}>Casual</Checkbox>
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Taxable Income P/A $:</FormLabel>
                  <Input value={formData.taxableIncomeA} onChange={(e) => handleInputChange("taxableIncomeA", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <FormControl>
                    <FormLabel fontSize="xs">Dependents:</FormLabel>
                    <Input value={formData.dependentsA} onChange={(e) => handleInputChange("dependentsA", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Ages:</FormLabel>
                    <Input value={formData.agesA} onChange={(e) => handleInputChange("agesA", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                </Grid>
              </VStack>
            </GridItem>

            {/* Applicant B */}
            <GridItem>
              <Text fontWeight="bold" fontSize="sm" color={brandConfig.colors.primary} mb={3}>Applicant B</Text>
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="xs">Name:</FormLabel>
                  <Input value={formData.nameB} onChange={(e) => handleInputChange("nameB", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Address:</FormLabel>
                  <Input value={formData.addressB} onChange={(e) => handleInputChange("addressB", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                  <FormControl>
                    <FormLabel fontSize="xs">Residence (Yrs):</FormLabel>
                    <Input value={formData.residenceYearsB} onChange={(e) => handleInputChange("residenceYearsB", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">State:</FormLabel>
                    <Input value={formData.stateB} onChange={(e) => handleInputChange("stateB", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">P/Code:</FormLabel>
                    <Input value={formData.postcodeB} onChange={(e) => handleInputChange("postcodeB", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                </Grid>
                <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                  <FormControl>
                    <FormLabel fontSize="xs">Ph (H):</FormLabel>
                    <Input value={formData.phoneHomeB} onChange={(e) => handleInputChange("phoneHomeB", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Mobile:</FormLabel>
                    <Input value={formData.mobileB} onChange={(e) => handleInputChange("mobileB", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">D.O.B:</FormLabel>
                    <Input value={formData.dobB} onChange={(e) => handleInputChange("dobB", e.target.value)} placeholder="DD/MM/YYYY" {...inputStyle} size="sm" />
                  </FormControl>
                </Grid>
                <FormControl>
                  <FormLabel fontSize="xs">Occupation:</FormLabel>
                  <Input value={formData.occupationB} onChange={(e) => handleInputChange("occupationB", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Employer:</FormLabel>
                  <Input value={formData.employerB} onChange={(e) => handleInputChange("employerB", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" fontWeight="bold">Employment:</FormLabel>
                  <HStack spacing={4}>
                    <Checkbox isChecked={formData.empFITB} onChange={(e) => handleInputChange("empFITB", e.target.checked)}>F/T</Checkbox>
                    <Checkbox isChecked={formData.empPTB} onChange={(e) => handleInputChange("empPTB", e.target.checked)}>P/T</Checkbox>
                    <Checkbox isChecked={formData.empCasualB} onChange={(e) => handleInputChange("empCasualB", e.target.checked)}>Casual</Checkbox>
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Taxable Income P/A $:</FormLabel>
                  <Input value={formData.taxableIncomeB} onChange={(e) => handleInputChange("taxableIncomeB", e.target.value)} {...inputStyle} size="sm" />
                </FormControl>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  <FormControl>
                    <FormLabel fontSize="xs">Dependents:</FormLabel>
                    <Input value={formData.dependentsB} onChange={(e) => handleInputChange("dependentsB", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Ages:</FormLabel>
                    <Input value={formData.agesB} onChange={(e) => handleInputChange("agesB", e.target.value)} {...inputStyle} size="sm" />
                  </FormControl>
                </Grid>
              </VStack>
            </GridItem>
          </Grid>

          <Divider my={4} />

          {/* ── SELF EMPLOYED & INCOME ── */}
          <Box bg={brandConfig.colors.primary} px={3} py={1} mb={4} borderRadius="sm">
            <Text color="white" fontWeight="bold" fontSize="sm">SELF EMPLOYED & INCOME</Text>
          </Box>

          <Grid templateColumns="auto auto auto 1fr" gap={4} alignItems="center" mb={3}>
            <Text fontSize="sm" fontWeight="bold">Self Employed:</Text>
            <Checkbox isChecked={formData.selfEmployedYes} onChange={(e) => handleInputChange("selfEmployedYes", e.target.checked)}>YES</Checkbox>
            <Checkbox isChecked={formData.selfEmployedNo} onChange={(e) => handleInputChange("selfEmployedNo", e.target.checked)}>NO</Checkbox>
            <FormControl>
              <FormLabel fontSize="xs">Annual Gross Turnover $:</FormLabel>
              <Input value={formData.grossTurnover} onChange={(e) => handleInputChange("grossTurnover", e.target.value)} {...inputStyle} size="sm" />
            </FormControl>
          </Grid>

          <Grid templateColumns="auto auto auto 1fr" gap={4} alignItems="center" mb={3}>
            <Text fontSize="sm" fontWeight="bold">Last 2yrs Tax Returns:</Text>
            <Checkbox isChecked={formData.taxReturnsYes} onChange={(e) => handleInputChange("taxReturnsYes", e.target.checked)}>YES</Checkbox>
            <Checkbox isChecked={formData.taxReturnsNo} onChange={(e) => handleInputChange("taxReturnsNo", e.target.checked)}>NO</Checkbox>
            <FormControl>
              <FormLabel fontSize="xs">Trading Name:</FormLabel>
              <Input value={formData.tradingName} onChange={(e) => handleInputChange("tradingName", e.target.value)} {...inputStyle} size="sm" />
            </FormControl>
          </Grid>

          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <FormControl>
              <FormLabel fontSize="xs">Net Taxable Income p.a $:</FormLabel>
              <Input value={formData.netTaxableIncome} onChange={(e) => handleInputChange("netTaxableIncome", e.target.value)} {...inputStyle} size="sm" />
            </FormControl>
            <HStack spacing={4} align="flex-end" pb={1}>
              <Text fontSize="sm" fontWeight="bold" whiteSpace="nowrap">Income Split:</Text>
              <Checkbox isChecked={formData.incomeSplitYes} onChange={(e) => handleInputChange("incomeSplitYes", e.target.checked)}>YES</Checkbox>
              <Checkbox isChecked={formData.incomeSplitNo} onChange={(e) => handleInputChange("incomeSplitNo", e.target.checked)}>NO</Checkbox>
              <FormControl>
                <FormLabel fontSize="xs">Split Amount $:</FormLabel>
                <Input value={formData.incomeSplitAmount} onChange={(e) => handleInputChange("incomeSplitAmount", e.target.value)} {...inputStyle} size="sm" />
              </FormControl>
            </HStack>
          </Grid>

          <Divider my={4} />

          {/* ── ASSETS ── */}
          <Box bg={brandConfig.colors.primary} px={3} py={1} mb={4} borderRadius="sm">
            <Text color="white" fontWeight="bold" fontSize="sm">ASSETS</Text>
          </Box>

          <TableContainer mb={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th {...thStyle} w="160px">Asset</Th>
                  <Th {...thStyle} w="130px">Value $</Th>
                  <Th {...thStyle}>Owner / Title</Th>
                  <Th {...thStyle} w="120px">Rent p.w. $</Th>
                </Tr>
              </Thead>
              <Tbody>
                <AssetRow label="House" valueField="assetHouseValue" ownerField="assetHouseOwner" rentField="assetHouseRent" />
                <AssetRow label="Property 1" valueField="assetProp1Value" ownerField="assetProp1Owner" rentField="assetProp1Rent" />
                <AssetRow label="Property 2" valueField="assetProp2Value" ownerField="assetProp2Owner" rentField="assetProp2Rent" />
                <AssetRow label="Property 3" valueField="assetProp3Value" ownerField="assetProp3Owner" rentField="assetProp3Rent" />
                <AssetRow label="Other Property 1" valueField="assetOtherProp1Value" ownerField="assetOtherProp1Owner" rentField="assetOtherProp1Rent" />
                <AssetRow label="Other Property 2" valueField="assetOtherProp2Value" ownerField="assetOtherProp2Owner" rentField="assetOtherProp2Rent" />
                <AssetRow label="Other Property 3" valueField="assetOtherProp3Value" ownerField="assetOtherProp3Owner" rentField="assetOtherProp3Rent" />
                <AssetRow label="Superannuation" valueField="assetSuperValue" ownerField="assetSuperOwner" />
                <AssetRow label="Shares" valueField="assetSharesValue" ownerField="assetSharesOwner" />
                <AssetRow label="Savings" valueField="assetSavingsValue" ownerField="assetSavingsOwner" />
                <AssetRow label="Other" valueField="assetOther1Value" ownerField="assetOther1Owner" />
                <AssetRow label="Other" valueField="assetOther2Value" ownerField="assetOther2Owner" />
              </Tbody>
            </Table>
          </TableContainer>

          <Divider my={4} />

          {/* ── LIABILITIES ── */}
          <Box bg={brandConfig.colors.primary} px={3} py={1} mb={4} borderRadius="sm">
            <Text color="white" fontWeight="bold" fontSize="sm">LIABILITIES</Text>
          </Box>

          <TableContainer mb={4}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th {...thStyle} w="140px">Liability</Th>
                  <Th {...thStyle}>Lender</Th>
                  <Th {...thStyle} w="110px">Balance $</Th>
                  <Th {...thStyle} w="120px">Limit Monthly $</Th>
                  <Th {...thStyle} w="120px">Repayment $</Th>
                </Tr>
              </Thead>
              <Tbody>
                <LiabRow label="House" lenderField="liabHouseLender" balanceField="liabHouseBalance" limitField="liabHouseLimit" repayField="liabHouseRepayment" />
                <LiabRow label="Property 1" lenderField="liabProp1Lender" balanceField="liabProp1Balance" limitField="liabProp1Limit" repayField="liabProp1Repayment" />
                <LiabRow label="Property 2" lenderField="liabProp2Lender" balanceField="liabProp2Balance" limitField="liabProp2Limit" repayField="liabProp2Repayment" />
                <LiabRow label="Property 3" lenderField="liabProp3Lender" balanceField="liabProp3Balance" limitField="liabProp3Limit" repayField="liabProp3Repayment" />
                <LiabRow label="Personal Loans" lenderField="liabPersonalLender" balanceField="liabPersonalBalance" limitField="liabPersonalLimit" repayField="liabPersonalRepayment" />
                <LiabRow label="Credit Card 1" lenderField="liabCC1Lender" balanceField="liabCC1Balance" limitField="liabCC1Limit" repayField="liabCC1Repayment" />
                <LiabRow label="Credit Card 2" lenderField="liabCC2Lender" balanceField="liabCC2Balance" limitField="liabCC2Limit" repayField="liabCC2Repayment" />
                <LiabRow label="HP / Lease" lenderField="liabHPLender" balanceField="liabHPBalance" limitField="liabHPLimit" repayField="liabHPRepayment" />
                <LiabRow label="Overdraft" lenderField="liabOverdraftLender" balanceField="liabOverdraftBalance" limitField="liabOverdraftLimit" repayField="liabOverdraftRepayment" />
                <LiabRow label="Priv. Health Ins." lenderField="liabPrivateHealthLender" balanceField="liabPrivateHealthBalance" limitField="liabPrivateHealthLimit" repayField="liabPrivateHealthRepayment" />
                <LiabRow label="Priv. School Fees" lenderField="liabPrivateSchoolLender" balanceField="liabPrivateSchoolBalance" limitField="liabPrivateSchoolLimit" repayField="liabPrivateSchoolRepayment" />
                <LiabRow label="Child Care Exp." lenderField="liabChildCareLender" balanceField="liabChildCareBalance" limitField="liabChildCareLimit" repayField="liabChildCareRepayment" />
                <LiabRow label="Other" lenderField="liabOther1Lender" balanceField="liabOther1Balance" limitField="liabOther1Limit" repayField="liabOther1Repayment" />
                <LiabRow label="Other" lenderField="liabOther2Lender" balanceField="liabOther2Balance" limitField="liabOther2Limit" repayField="liabOther2Repayment" />
                <LiabRow label="Other" lenderField="liabOther3Lender" balanceField="liabOther3Balance" limitField="liabOther3Limit" repayField="liabOther3Repayment" />
              </Tbody>
            </Table>
          </TableContainer>

          <Divider my={4} />

          {/* ── PRIVACY ACT ── */}
          <Box p={4} bg={brandConfig.colors.background.surface} border="1px solid" borderColor="rgba(0, 0, 0, 0.1)" borderRadius="md" mb={6}>
            <Text fontSize="sm" fontWeight="bold" mb={2}>PRIVACY ACT</Text>
            <Text fontSize="sm">
              I/We authorise release of the information contained herein to such parties as are necessary to assess and
              provide advice regarding property investment provided that such information is treated as confidential and
              restricted to such parties as are affiliated with One Property Australia.
            </Text>
          </Box>

          {/* ── SIGNATURES ── */}
          <Grid templateColumns="1fr 1fr" gap={6} mb={4}>
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1}>Signature (Applicant A):</Text>
              <Box h="60px" bg={brandConfig.colors.background.surface} border="1px solid" borderColor="rgba(0, 0, 0, 0.1)" borderRadius="md" />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={1}>Signature (Applicant B):</Text>
              <Box h="60px" bg={brandConfig.colors.background.surface} border="1px solid" borderColor="rgba(0, 0, 0, 0.1)" borderRadius="md" />
            </Box>
          </Grid>

          <Grid templateColumns="1fr 1fr" gap={6}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Consultant Name:</FormLabel>
              <Input value={formData.consultantName} onChange={(e) => handleInputChange("consultantName", e.target.value)} {...inputStyle} size="sm" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">Date:</FormLabel>
              <Input value={formData.date} onChange={(e) => handleInputChange("date", e.target.value)} placeholder="DD/MM/YYYY" {...inputStyle} size="sm" />
            </FormControl>
          </Grid>

        </Box>
      </Container>

      <Box className="no-print">
        <FooterWithFourColumns />
      </Box>

      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              margin: 0;
              padding: 0;
            }
            #al-form-printable {
              border: none !important;
              box-shadow: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default AandLForm;
